/** One function's provider, model, effort and key (SA-1087 §12). The page keys this by the
 * function id so typed text never carries to another function. Test and Save send the
 * same body; a refusal's sentence is printed under the form. */
import { useState, type FormEvent } from 'react'
import {
  Alert, AlertDescription, Button, Input, NativeSelect,
  Modal, ModalBody, ModalContent, ModalDescription, ModalFooter, ModalHeader, ModalTitle,
} from '@/components/ui'
import { useClearAiModel, useProviderModels, useSaveAiModel, useTestAiModel } from '@/hooks/useAiModels'
import type { AiModelFunction, AiModelKeySource, AiModelWrite, AiModelsRead } from '@/types/aiModels'
import * as text from './aiModelsText'

interface Form {
  provider: string
  base_url: string
  model: string
  effort: string
  key_source: AiModelKeySource
  key_service: string
  api_key: string
}

function initial(fn: AiModelFunction): Form {
  const from = fn.saved ?? fn.effective
  return {
    provider: from && fn.providers.includes(from.provider) ? from.provider : fn.providers[0],
    base_url: (fn.saved?.base_url ?? (fn.effective?.provider === 'openai_compatible' ? fn.effective.base_url : '')) ?? '',
    model: from?.model ?? '',
    effort: from?.effort ?? '',
    key_source: from && fn.key_sources.includes(from.key_source) ? from.key_source : fn.key_sources[0] ?? 'actor',
    key_service: from?.key_service ?? '',
    api_key: '',
  }
}

function toWrite(f: Form): AiModelWrite {
  const compatible = f.provider === 'openai_compatible'
  return {
    provider: f.provider,
    base_url: compatible ? f.base_url.trim() || null : null,
    model: f.model.trim(),
    effort: f.effort || null,
    key_source: f.key_source,
    key_service: f.key_source === 'actor' && compatible && f.key_service.trim() ? f.key_service.trim() : null,
    api_key: f.key_source === 'deployment' && f.api_key.trim() ? f.api_key.trim() : null,
  }
}

export function AiModelDialog({ fn, read, onClose }: { fn: AiModelFunction | null; read: AiModelsRead; onClose: () => void }) {
  const [form, setForm] = useState<Form>(() => (fn ? initial(fn) : { provider: 'openai', base_url: '', model: '', effort: '', key_source: 'actor', key_service: '', api_key: '' }))
  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const save = useSaveAiModel()
  const test = useTestAiModel()
  const clear = useClearAiModel()
  const models = useProviderModels()
  const [loaded, setLoaded] = useState<{ provider: string; base_url: string; models: string[] } | null>(null)
  if (!fn) return null
  const set = (patch: Partial<Form>) => { setForm((f) => ({ ...f, ...patch })); setNote(null); setError(null) }
  const providers = read.providers.filter((p) => fn.providers.includes(p.id))
  const providerLabel = providers.find((p) => p.id === form.provider)?.label ?? form.provider
  const busy = save.isPending || test.isPending || clear.isPending || models.isPending
  const from = fn.saved ?? fn.effective
  const keyStored = from?.key_source === 'deployment' && from.key_set
  const compatible = form.provider === 'openai_compatible'
  // The selected provider's effort levels (SA-1115): Anthropic takes its own five.
  const effortsFor = (provider: string) => read.providers.find((p) => p.id === provider)?.efforts ?? read.efforts
  const vision = text.visionReplaces(fn)

  const onTest = async () => {
    setError(null); setNote(null)
    try {
      const out = await test.mutateAsync({ id: fn.id, body: toWrite(form) })
      setNote(text.testOk(out.latency_ms))
    } catch (err) { setError(text.testErrorText(err)) }
  }
  const onLoadModels = async () => {
    setError(null); setNote(null)
    try {
      const out = await models.mutateAsync({ id: fn.id, body: toWrite(form) })
      setLoaded({ provider: form.provider, base_url: form.base_url, models: out.models })
      setNote(text.modelsLoaded(out.models.length, providerLabel))
    } catch (err) { setError(text.modelsErrorText(err)) }
  }
  // A list belongs to the provider and URL it was read for.
  const suggestions = loaded && loaded.provider === form.provider && loaded.base_url === form.base_url
    ? loaded.models : text.CANDIDATE_MODELS
  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault(); setError(null)
    try { await save.mutateAsync({ id: fn.id, body: toWrite(form) }); onClose() } catch (err) { setError(text.refusalText(err)) }
  }
  const onInherit = async () => {
    setError(null)
    try { await clear.mutateAsync(fn.id); onClose() } catch (err) { setError(text.refusalText(err)) }
  }

  return (
    <Modal open onOpenChange={(open) => !open && onClose()}>
      <ModalContent size="md">
        <ModalHeader>
          <ModalTitle>{fn.label}</ModalTitle>
          <ModalDescription>{fn.tools ? text.TOOLS_HINT : text.PAGE_SENTENCE}</ModalDescription>
        </ModalHeader>
        <form onSubmit={onSubmit}>
          <ModalBody className="space-y-4">
            <NativeSelect label={text.PROVIDER} value={form.provider} onChange={(e) => {
              const provider = e.target.value
              set({ provider, effort: effortsFor(provider).includes(form.effort) ? form.effort : '' })
            }}>
              {providers.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </NativeSelect>
            {compatible && (
              <Input label={text.BASE_URL} value={form.base_url} placeholder={text.BASE_URL_PLACEHOLDER} onChange={(e) => set({ base_url: e.target.value })} />
            )}
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Input label={text.MODEL} value={form.model} list="ai-models-candidates" onChange={(e) => set({ model: e.target.value })} />
              </div>
              <Button type="button" variant="outline" disabled={busy} onClick={onLoadModels}>
                {models.isPending ? text.LOADING_MODELS : text.LOAD_MODELS}
              </Button>
            </div>
            <datalist id="ai-models-candidates">{suggestions.map((m) => <option key={m} value={m} />)}</datalist>
            {vision && <p className="text-xs text-muted-foreground">{vision}</p>}
            <NativeSelect label={text.EFFORT} value={form.effort} onChange={(e) => set({ effort: e.target.value })}>
              <option value="">{text.MODEL_DEFAULT_EFFORT}</option>
              {effortsFor(form.provider).map((v) => <option key={v} value={v}>{v}</option>)}
            </NativeSelect>
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">{text.KEY_SOURCE}</legend>
              {fn.key_sources.includes('actor') && (
                <label className="flex items-center gap-2 text-sm">
                  <input type="radio" name="key_source" checked={form.key_source === 'actor'} onChange={() => set({ key_source: 'actor' })} />
                  {text.ACTOR_KEY(fn.actor)}
                </label>
              )}
              {fn.key_sources.includes('deployment') && (
                <label className="flex items-center gap-2 text-sm">
                  <input type="radio" name="key_source" checked={form.key_source === 'deployment'} onChange={() => set({ key_source: 'deployment' })} />
                  {text.STORED_KEY}
                </label>
              )}
              {form.key_source === 'deployment' && (
                <Input
                  type="password" label={text.API_KEY} value={form.api_key} autoComplete="off"
                  placeholder={keyStored ? text.KEY_SET_PLACEHOLDER : text.KEY_PLACEHOLDER}
                  onChange={(e) => set({ api_key: e.target.value })}
                />
              )}
              {form.key_source === 'actor' && compatible && (
                <Input label={text.KEY_SERVICE} value={form.key_service} placeholder={text.KEY_SERVICE_PLACEHOLDER} onChange={(e) => set({ key_service: e.target.value })} />
              )}
              {form.key_source === 'actor' && <p className="text-xs text-muted-foreground">{text.testsWith(fn, providerLabel)}</p>}
            </fieldset>
            <p role="status" className="text-sm text-muted-foreground">{note ?? ''}</p>
            {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
          </ModalBody>
          <ModalFooter>
            {fn.recommended && !fn.saved && (
              <Button type="button" variant="ghost" onClick={() => set({ model: fn.recommended!.model, effort: fn.recommended!.effort ?? '' })}>
                {text.USE_RECOMMENDED}
              </Button>
            )}
            {fn.saved && <Button type="button" variant="ghost" disabled={busy} onClick={onInherit}>{text.USE_INHERITED}</Button>}
            <Button type="button" variant="outline" disabled={save.isPending} onClick={onClose}>{text.CANCEL}</Button>
            <Button type="button" variant="outline" disabled={busy} onClick={onTest}>{test.isPending ? text.TESTING : text.TEST}</Button>
            <Button type="submit" disabled={busy}>{save.isPending ? text.SAVING : text.SAVE}</Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  )
}
